import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Plus,
  Trash2,
  Edit2,
  X,
  Loader2,
  GripVertical,
  Check,
  CheckCircle,
  XCircle,
  Sparkles,
  ImageOff,
  Image,
} from 'lucide-react';
import { getSupabaseAdminClient } from '@parentpath/supabase-client';
import toast from 'react-hot-toast';
import { ImageUpload } from '@/components/ui/ImageUpload';
import { LocaleSelector } from '@/components/LocaleSelector';
import { useAdminLocale } from '@/hooks/useAdminLocale';

const supabase = getSupabaseAdminClient();

type ContentType = 'feature' | 'slideshow_image';

interface PremiumContent {
  id: string;
  content_type: ContentType;
  content_value: Record<string, unknown>;
  sort_order: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

interface FeatureValue {
  name: string;
  free: boolean | string;
  premium: boolean | string;
}

interface SlideshowValue {
  image_url: string;
  alt_text: string;
}

// ====================
// Feature Modal
// ====================
interface FeatureModalProps {
  isOpen: boolean;
  onClose: () => void;
  existingFeature?: PremiumContent | null;
  nextSortOrder: number;
  locale: string;
}

function FeatureModal({ isOpen, onClose, existingFeature, nextSortOrder, locale }: FeatureModalProps) {
  const queryClient = useQueryClient();
  const existingValue = existingFeature?.content_value as FeatureValue | undefined;

  const [name, setName] = useState(existingValue?.name || '');
  const [freeType, setFreeType] = useState<'boolean' | 'string'>(
    typeof existingValue?.free === 'string' ? 'string' : 'boolean'
  );
  const [freeBoolean, setFreeBoolean] = useState(
    typeof existingValue?.free === 'boolean' ? existingValue.free : false
  );
  const [freeString, setFreeString] = useState(
    typeof existingValue?.free === 'string' ? existingValue.free : ''
  );
  const [premiumType, setPremiumType] = useState<'boolean' | 'string'>(
    typeof existingValue?.premium === 'string' ? 'string' : 'boolean'
  );
  const [premiumBoolean, setPremiumBoolean] = useState(
    typeof existingValue?.premium === 'boolean' ? existingValue.premium : true
  );
  const [premiumString, setPremiumString] = useState(
    typeof existingValue?.premium === 'string' ? existingValue.premium : ''
  );

  const createMutation = useMutation({
    mutationFn: async () => {
      const contentValue: FeatureValue = {
        name,
        free: freeType === 'boolean' ? freeBoolean : freeString,
        premium: premiumType === 'boolean' ? premiumBoolean : premiumString,
      };

      const { error } = await supabase
        .from('premium_content')
        .insert({
          content_type: 'feature',
          content_value: contentValue,
          sort_order: nextSortOrder,
          is_active: true,
          locale,
        } as never);

      if (error) throw error;
    },
    onSuccess: () => {
      toast.success('Feature erstellt');
      queryClient.invalidateQueries({ queryKey: ['premium-content'] });
      onClose();
    },
    onError: () => {
      toast.error('Fehler beim Erstellen');
    },
  });

  const updateMutation = useMutation({
    mutationFn: async () => {
      const contentValue: FeatureValue = {
        name,
        free: freeType === 'boolean' ? freeBoolean : freeString,
        premium: premiumType === 'boolean' ? premiumBoolean : premiumString,
      };

      const { error } = await supabase
        .from('premium_content')
        .update({
          content_value: contentValue,
          updated_at: new Date().toISOString(),
        } as never)
        .eq('id', existingFeature!.id);

      if (error) throw error;
    },
    onSuccess: () => {
      toast.success('Feature aktualisiert');
      queryClient.invalidateQueries({ queryKey: ['premium-content'] });
      onClose();
    },
    onError: () => {
      toast.error('Fehler beim Aktualisieren');
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) {
      toast.error('Name ist erforderlich');
      return;
    }
    if (existingFeature) {
      updateMutation.mutate();
    } else {
      createMutation.mutate();
    }
  };

  if (!isOpen) return null;

  const isLoading = createMutation.isPending || updateMutation.isPending;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
        <div className="p-6 border-b border-slate-200">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-bold text-slate-900">
              {existingFeature ? 'Feature bearbeiten' : 'Neues Feature'}
            </h2>
            <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-lg">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Feature Name */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Feature Name
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="z.B. KI-Assistent"
              className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 outline-none"
              required
            />
          </div>

          {/* Free Value */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Free-Wert
            </label>
            <div className="flex gap-2 mb-2">
              <button
                type="button"
                onClick={() => setFreeType('boolean')}
                className={`px-3 py-1 rounded-lg text-sm ${
                  freeType === 'boolean'
                    ? 'bg-purple-600 text-white'
                    : 'bg-slate-100 text-slate-600'
                }`}
              >
                Ja/Nein
              </button>
              <button
                type="button"
                onClick={() => setFreeType('string')}
                className={`px-3 py-1 rounded-lg text-sm ${
                  freeType === 'string'
                    ? 'bg-purple-600 text-white'
                    : 'bg-slate-100 text-slate-600'
                }`}
              >
                Text
              </button>
            </div>
            {freeType === 'boolean' ? (
              <div className="flex gap-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    checked={freeBoolean}
                    onChange={() => setFreeBoolean(true)}
                    className="text-purple-600"
                  />
                  <span className="text-sm">Ja (Haken)</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    checked={!freeBoolean}
                    onChange={() => setFreeBoolean(false)}
                    className="text-purple-600"
                  />
                  <span className="text-sm">Nein (X)</span>
                </label>
              </div>
            ) : (
              <input
                type="text"
                value={freeString}
                onChange={(e) => setFreeString(e.target.value)}
                placeholder="z.B. 3 Nachrichten/Tag"
                className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 outline-none"
              />
            )}
          </div>

          {/* Premium Value */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Premium-Wert
            </label>
            <div className="flex gap-2 mb-2">
              <button
                type="button"
                onClick={() => setPremiumType('boolean')}
                className={`px-3 py-1 rounded-lg text-sm ${
                  premiumType === 'boolean'
                    ? 'bg-purple-600 text-white'
                    : 'bg-slate-100 text-slate-600'
                }`}
              >
                Ja/Nein
              </button>
              <button
                type="button"
                onClick={() => setPremiumType('string')}
                className={`px-3 py-1 rounded-lg text-sm ${
                  premiumType === 'string'
                    ? 'bg-purple-600 text-white'
                    : 'bg-slate-100 text-slate-600'
                }`}
              >
                Text
              </button>
            </div>
            {premiumType === 'boolean' ? (
              <div className="flex gap-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    checked={premiumBoolean}
                    onChange={() => setPremiumBoolean(true)}
                    className="text-purple-600"
                  />
                  <span className="text-sm">Ja (Haken)</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    checked={!premiumBoolean}
                    onChange={() => setPremiumBoolean(false)}
                    className="text-purple-600"
                  />
                  <span className="text-sm">Nein (X)</span>
                </label>
              </div>
            ) : (
              <input
                type="text"
                value={premiumString}
                onChange={(e) => setPremiumString(e.target.value)}
                placeholder="z.B. Unbegrenzt"
                className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 outline-none"
              />
            )}
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg font-medium"
            >
              Abbrechen
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="flex-1 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg font-medium disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {isLoading && <Loader2 className="w-4 h-4 animate-spin" />}
              {existingFeature ? 'Speichern' : 'Erstellen'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ====================
// Slideshow Image Modal
// ====================
interface SlideshowModalProps {
  isOpen: boolean;
  onClose: () => void;
  existingSlide?: PremiumContent | null;
  nextSortOrder: number;
  locale: string;
}

function SlideshowModal({ isOpen, onClose, existingSlide, nextSortOrder, locale }: SlideshowModalProps) {
  const queryClient = useQueryClient();
  const existingValue = existingSlide?.content_value as SlideshowValue | undefined;

  const [imageUrl, setImageUrl] = useState(existingValue?.image_url || '');
  const [altText, setAltText] = useState(existingValue?.alt_text || '');

  const createMutation = useMutation({
    mutationFn: async () => {
      const contentValue: SlideshowValue = {
        image_url: imageUrl,
        alt_text: altText,
      };

      const { error } = await supabase
        .from('premium_content')
        .insert({
          content_type: 'slideshow_image',
          content_value: contentValue,
          sort_order: nextSortOrder,
          is_active: true,
          locale,
        } as never);

      if (error) throw error;
    },
    onSuccess: () => {
      toast.success('Bild hinzugefügt');
      queryClient.invalidateQueries({ queryKey: ['premium-content'] });
      onClose();
    },
    onError: () => {
      toast.error('Fehler beim Erstellen');
    },
  });

  const updateMutation = useMutation({
    mutationFn: async () => {
      const contentValue: SlideshowValue = {
        image_url: imageUrl,
        alt_text: altText,
      };

      const { error } = await supabase
        .from('premium_content')
        .update({
          content_value: contentValue,
          updated_at: new Date().toISOString(),
        } as never)
        .eq('id', existingSlide!.id);

      if (error) throw error;
    },
    onSuccess: () => {
      toast.success('Bild aktualisiert');
      queryClient.invalidateQueries({ queryKey: ['premium-content'] });
      onClose();
    },
    onError: () => {
      toast.error('Fehler beim Aktualisieren');
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!imageUrl.trim()) {
      toast.error('Bild-URL ist erforderlich');
      return;
    }
    if (existingSlide) {
      updateMutation.mutate();
    } else {
      createMutation.mutate();
    }
  };

  if (!isOpen) return null;

  const isLoading = createMutation.isPending || updateMutation.isPending;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
        <div className="p-6 border-b border-slate-200">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-bold text-slate-900">
              {existingSlide ? 'Bild bearbeiten' : 'Neues Bild'}
            </h2>
            <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-lg">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Image Upload */}
          <ImageUpload
            value={imageUrl}
            onChange={setImageUrl}
            bucket="PremiumFlowImages"
            label="Slideshow-Bild"
            placeholder="https://example.com/image.jpg"
          />

          {/* Alt Text */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Alt-Text (Barrierefreiheit)
            </label>
            <input
              type="text"
              value={altText}
              onChange={(e) => setAltText(e.target.value)}
              placeholder="z.B. Premium Feature Übersicht"
              className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 outline-none"
            />
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg font-medium"
            >
              Abbrechen
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="flex-1 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg font-medium disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {isLoading && <Loader2 className="w-4 h-4 animate-spin" />}
              {existingSlide ? 'Speichern' : 'Hinzufügen'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ====================
// Main Page
// ====================
export function PremiumContentPage() {
  const queryClient = useQueryClient();
  const { contentLocale } = useAdminLocale();
  const [showFeatureModal, setShowFeatureModal] = useState(false);
  const [showSlideshowModal, setShowSlideshowModal] = useState(false);
  const [editingFeature, setEditingFeature] = useState<PremiumContent | null>(null);
  const [editingSlide, setEditingSlide] = useState<PremiumContent | null>(null);

  // Fetch premium content for selected locale
  const { data: content, isLoading } = useQuery({
    queryKey: ['premium-content', contentLocale],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('premium_content')
        .select('*')
        .eq('locale', contentLocale)
        .order('sort_order', { ascending: true });

      if (error) throw error;
      return data as PremiumContent[];
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('premium_content')
        .delete()
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      toast.success('Eintrag gelöscht');
      queryClient.invalidateQueries({ queryKey: ['premium-content'] });
    },
    onError: () => {
      toast.error('Fehler beim Löschen');
    },
  });

  // Toggle active mutation
  const toggleActiveMutation = useMutation({
    mutationFn: async ({ id, isActive }: { id: string; isActive: boolean }) => {
      const { error } = await supabase
        .from('premium_content')
        .update({ is_active: isActive } as never)
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: (_, { isActive }) => {
      toast.success(isActive ? 'Aktiviert' : 'Deaktiviert');
      queryClient.invalidateQueries({ queryKey: ['premium-content'] });
    },
    onError: () => {
      toast.error('Fehler beim Aktualisieren');
    },
  });

  // Update sort order mutation
  const updateSortMutation = useMutation({
    mutationFn: async ({ id, sortOrder }: { id: string; sortOrder: number }) => {
      const { error } = await supabase
        .from('premium_content')
        .update({ sort_order: sortOrder } as never)
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['premium-content'] });
    },
  });

  const handleDelete = (id: string, type: ContentType) => {
    const typeLabel = type === 'feature' ? 'Feature' : 'Bild';
    if (confirm(`${typeLabel} wirklich löschen?`)) {
      deleteMutation.mutate(id);
    }
  };

  const handleMoveUp = (item: PremiumContent, items: PremiumContent[]) => {
    const currentIndex = items.findIndex(i => i.id === item.id);
    if (currentIndex <= 0) return;

    const prevItem = items[currentIndex - 1];
    updateSortMutation.mutate({ id: item.id, sortOrder: prevItem.sort_order });
    updateSortMutation.mutate({ id: prevItem.id, sortOrder: item.sort_order });
  };

  const handleMoveDown = (item: PremiumContent, items: PremiumContent[]) => {
    const currentIndex = items.findIndex(i => i.id === item.id);
    if (currentIndex >= items.length - 1) return;

    const nextItem = items[currentIndex + 1];
    updateSortMutation.mutate({ id: item.id, sortOrder: nextItem.sort_order });
    updateSortMutation.mutate({ id: nextItem.id, sortOrder: item.sort_order });
  };

  // Split content by type
  const features = content?.filter(c => c.content_type === 'feature') || [];
  const slides = content?.filter(c => c.content_type === 'slideshow_image') || [];

  const nextFeatureSortOrder = features.length > 0
    ? Math.max(...features.map(f => f.sort_order)) + 1
    : 1;
  const nextSlideSortOrder = slides.length > 0
    ? Math.max(...slides.map(s => s.sort_order)) + 1
    : 1;

  // Render feature value
  const renderFeatureValue = (value: boolean | string) => {
    if (typeof value === 'boolean') {
      return value ? (
        <CheckCircle className="w-5 h-5 text-emerald-500" />
      ) : (
        <XCircle className="w-5 h-5 text-slate-300" />
      );
    }
    return <span className="text-sm text-slate-600">{value}</span>;
  };

  return (
    <div>
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-2">
          <h1 className="text-3xl font-bold text-slate-900">Premium-Flow</h1>
          <LocaleSelector />
        </div>
        <p className="text-slate-600">
          Verwalte die Inhalte des Premium-Angebots im Onboarding
        </p>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 text-purple-600 animate-spin" />
        </div>
      ) : (
        <div className="space-y-8">
          {/* Slideshow Section */}
          <div className="bg-white rounded-lg shadow-sm overflow-hidden">
            <div className="p-6 border-b border-slate-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                    <Image className="w-5 h-5 text-purple-600" />
                  </div>
                  <div>
                    <h2 className="text-lg font-semibold text-slate-900">Slideshow-Bilder</h2>
                    <p className="text-sm text-slate-500">{slides.length} Bilder</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowSlideshowModal(true)}
                  className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg font-medium flex items-center gap-2"
                >
                  <Plus className="w-4 h-4" />
                  Bild hinzufügen
                </button>
              </div>
            </div>

            {slides.length > 0 ? (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 p-6">
                {slides.map((slide, index) => {
                  const value = slide.content_value as unknown as SlideshowValue;
                  return (
                    <div
                      key={slide.id}
                      className={`relative group rounded-lg overflow-hidden border-2 ${
                        slide.is_active ? 'border-purple-200' : 'border-slate-200 opacity-50'
                      }`}
                    >
                      <div className="aspect-video bg-slate-100">
                        <img
                          src={value.image_url}
                          alt={value.alt_text}
                          className="w-full h-full object-cover object-center"
                          onError={(e) => {
                            (e.target as HTMLImageElement).style.display = 'none';
                            (e.target as HTMLImageElement).nextElementSibling?.classList.remove('hidden');
                          }}
                        />
                        <div className="hidden w-full h-full flex-col items-center justify-center text-slate-400">
                          <ImageOff className="w-8 h-8 mb-1" />
                          <span className="text-xs">Fehler</span>
                        </div>
                      </div>

                      {/* Order indicator */}
                      <div className="absolute top-2 left-2 w-6 h-6 bg-black/50 rounded-full flex items-center justify-center">
                        <span className="text-xs text-white font-medium">{index + 1}</span>
                      </div>

                      {/* Actions overlay */}
                      <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                        <button
                          onClick={() => handleMoveUp(slide, slides)}
                          disabled={index === 0}
                          className="p-2 bg-white rounded-lg hover:bg-slate-100 disabled:opacity-50"
                          title="Nach oben"
                        >
                          <GripVertical className="w-4 h-4 rotate-180" />
                        </button>
                        <button
                          onClick={() => handleMoveDown(slide, slides)}
                          disabled={index === slides.length - 1}
                          className="p-2 bg-white rounded-lg hover:bg-slate-100 disabled:opacity-50"
                          title="Nach unten"
                        >
                          <GripVertical className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => setEditingSlide(slide)}
                          className="p-2 bg-white rounded-lg hover:bg-slate-100"
                          title="Bearbeiten"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => toggleActiveMutation.mutate({ id: slide.id, isActive: !slide.is_active })}
                          className={`p-2 rounded-lg ${
                            slide.is_active
                              ? 'bg-emerald-100 text-emerald-600'
                              : 'bg-white text-slate-400'
                          }`}
                          title={slide.is_active ? 'Deaktivieren' : 'Aktivieren'}
                        >
                          <Check className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(slide.id, 'slideshow_image')}
                          className="p-2 bg-red-100 text-red-600 rounded-lg hover:bg-red-200"
                          title="Löschen"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-12">
                <Image className="w-12 h-12 text-slate-300 mb-4" />
                <p className="text-slate-500 mb-4">Noch keine Slideshow-Bilder</p>
                <button
                  onClick={() => setShowSlideshowModal(true)}
                  className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg font-medium flex items-center gap-2"
                >
                  <Plus className="w-4 h-4" />
                  Erstes Bild hinzufügen
                </button>
              </div>
            )}
          </div>

          {/* Features Section */}
          <div className="bg-white rounded-lg shadow-sm overflow-hidden">
            <div className="p-6 border-b border-slate-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                    <Sparkles className="w-5 h-5 text-amber-600" />
                  </div>
                  <div>
                    <h2 className="text-lg font-semibold text-slate-900">Feature-Vergleich</h2>
                    <p className="text-sm text-slate-500">{features.length} Features</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowFeatureModal(true)}
                  className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg font-medium flex items-center gap-2"
                >
                  <Plus className="w-4 h-4" />
                  Feature hinzufügen
                </button>
              </div>
            </div>

            {features.length > 0 ? (
              <table className="w-full">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-slate-600 w-12">#</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-slate-600">Feature</th>
                    <th className="px-4 py-3 text-center text-sm font-semibold text-slate-600 w-32">Free</th>
                    <th className="px-4 py-3 text-center text-sm font-semibold text-slate-600 w-32">Premium</th>
                    <th className="px-4 py-3 text-center text-sm font-semibold text-slate-600 w-24">Status</th>
                    <th className="px-4 py-3 text-right text-sm font-semibold text-slate-600 w-40">Aktionen</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {features.map((feature, index) => {
                    const value = feature.content_value as unknown as FeatureValue;
                    return (
                      <tr key={feature.id} className={`hover:bg-slate-50 ${!feature.is_active ? 'opacity-50' : ''}`}>
                        <td className="px-4 py-3">
                          <span className="w-6 h-6 bg-slate-100 rounded-full flex items-center justify-center text-xs font-medium text-slate-600">
                            {index + 1}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <span className="font-medium text-slate-900">{value.name}</span>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex justify-center">
                            {renderFeatureValue(value.free)}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex justify-center">
                            {renderFeatureValue(value.premium)}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex justify-center">
                            <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                              feature.is_active
                                ? 'bg-emerald-100 text-emerald-700'
                                : 'bg-slate-100 text-slate-600'
                            }`}>
                              {feature.is_active ? 'Aktiv' : 'Inaktiv'}
                            </span>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center justify-end gap-1">
                            <button
                              onClick={() => handleMoveUp(feature, features)}
                              disabled={index === 0}
                              className="p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded disabled:opacity-30"
                              title="Nach oben"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                              </svg>
                            </button>
                            <button
                              onClick={() => handleMoveDown(feature, features)}
                              disabled={index === features.length - 1}
                              className="p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded disabled:opacity-30"
                              title="Nach unten"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                              </svg>
                            </button>
                            <button
                              onClick={() => toggleActiveMutation.mutate({ id: feature.id, isActive: !feature.is_active })}
                              className={`p-1.5 rounded hover:bg-slate-100 ${
                                feature.is_active ? 'text-emerald-600' : 'text-slate-400'
                              }`}
                              title={feature.is_active ? 'Deaktivieren' : 'Aktivieren'}
                            >
                              <CheckCircle className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => setEditingFeature(feature)}
                              className="p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded"
                              title="Bearbeiten"
                            >
                              <Edit2 className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleDelete(feature.id, 'feature')}
                              className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded"
                              title="Löschen"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            ) : (
              <div className="flex flex-col items-center justify-center py-12">
                <Sparkles className="w-12 h-12 text-slate-300 mb-4" />
                <p className="text-slate-500 mb-4">Noch keine Features definiert</p>
                <button
                  onClick={() => setShowFeatureModal(true)}
                  className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg font-medium flex items-center gap-2"
                >
                  <Plus className="w-4 h-4" />
                  Erstes Feature hinzufügen
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Modals */}
      <FeatureModal
        isOpen={showFeatureModal || !!editingFeature}
        onClose={() => {
          setShowFeatureModal(false);
          setEditingFeature(null);
        }}
        existingFeature={editingFeature}
        nextSortOrder={nextFeatureSortOrder}
        locale={contentLocale}
      />

      <SlideshowModal
        isOpen={showSlideshowModal || !!editingSlide}
        onClose={() => {
          setShowSlideshowModal(false);
          setEditingSlide(null);
        }}
        existingSlide={editingSlide}
        nextSortOrder={nextSlideSortOrder}
        locale={contentLocale}
      />
    </div>
  );
}
